import React from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";

import { Layout, Loading } from "./components.jsx";
import { useAuth } from "./AuthContext.jsx";

import Dashboard from "./pages/Dashboard.jsx";
import Login from "./pages/Login.jsx";
import Signup from "./pages/Signup.jsx";
import AdminOrganizations from "./pages/AdminOrganizations.jsx";
import Users from "./pages/Users.jsx";
import ApiKeys from "./pages/ApiKeys.jsx";
import LibraryShell from "./pages/LibraryShell.jsx";
import LibraryGlobalRules from "./pages/LibraryGlobalRules.jsx";
import LibrarySpecialties from "./pages/LibrarySpecialties.jsx";
import LibrarySpecialtyDetail from "./pages/LibrarySpecialtyDetail.jsx";
import LibrarySpecialtyNew from "./pages/LibrarySpecialtyNew.jsx";
import LibrarySpecialtyAgent from "./pages/LibrarySpecialtyAgent.jsx";
import LibraryProtocols from "./pages/LibraryProtocols.jsx";
import LibraryProtocolDetail from "./pages/LibraryProtocolDetail.jsx";
import LibraryProtocolNew from "./pages/LibraryProtocolNew.jsx";
import LibraryAgents from "./pages/LibraryAgents.jsx";
import LibraryAgentNew from "./pages/LibraryAgentNew.jsx";
import LibraryAgentDetail from "./pages/LibraryAgentDetail.jsx";
import LibraryAgentRouter from "./pages/LibraryAgentRouter.jsx";
import LibraryTools from "./pages/LibraryTools.jsx";
import LibraryToolEdit from "./pages/LibraryToolEdit.jsx";
import LibraryPostCallActions from "./pages/LibraryPostCallActions.jsx";
import LibraryChainFramework from "./pages/LibraryChainFramework.jsx";
import StructuredAgentEditor from "./pages/StructuredAgentEditor.jsx";
import WorkspacePage from "./pages/WorkspacePage.jsx";
import LibraryDraftRevisions from "./pages/LibraryDraftRevisions.jsx";
import LibraryLiveVersions from "./pages/LibraryLiveVersions.jsx";
import Tenants from "./pages/Tenants.jsx";
import TenantNew from "./pages/TenantNew.jsx";
import TenantUpdates from "./pages/TenantUpdates.jsx";
import TenantDetail from "./pages/TenantDetail.jsx";
import SubTenantDetail from "./pages/SubTenantDetail.jsx";
import SubTenantPromptEdit from "./pages/SubTenantPromptEdit.jsx";
import TestingSetup from "./pages/TestingSetup.jsx";
import { LibraryEnvProvider } from "./LibraryEnvContext.jsx";
import GitHubSettings from "./pages/GitHubSettings.jsx";
import NotFound from "./pages/NotFound.jsx";

function LibraryRoute({ children }) {
  return (
    <RequireOrg>
      <LibraryEnvProvider>
        <LibraryShell>{children}</LibraryShell>
      </LibraryEnvProvider>
    </RequireOrg>
  );
}

function RequireOrg({ children }) {
  const { inOrgContext, isPlatformAdmin } = useAuth();
  if (inOrgContext) return children;
  // No active org — platform admin should pick one; org users shouldn't
  // land here but we fall through to a friendly message just in case.
  if (isPlatformAdmin) {
    return <Navigate to="/admin/organizations" replace />;
  }
  return (
    <div className="page-body">
      <div className="empty-state">
        <h3>No active organization</h3>
        <p>Contact your platform administrator.</p>
      </div>
    </div>
  );
}

function Protected({ children, requireRole }) {
  const { user, loading } = useAuth();
  const loc = useLocation();
  if (loading) {
    return (
      <div style={{ padding: 40 }}>
        <Loading label="Loading..." />
      </div>
    );
  }
  if (!user) {
    return <Navigate to="/login" replace state={{ from: loc }} />;
  }
  if (requireRole && user.role !== requireRole) {
    return (
      <div className="page-body">
        <div className="empty-state">
          <h3>Access denied</h3>
          <p>This page requires role: {requireRole}.</p>
        </div>
      </div>
    );
  }
  return children;
}

export default function App() {
  const { user, loading } = useAuth();
  const loc = useLocation();

  // Public auth pages — render without the Layout shell
  if (loc.pathname === "/login") return <Login />;
  if (loc.pathname === "/signup") return <Signup />;

  // Block rendering until the /auth/me check resolves so we don't
  // flash the sidebar for a moment before redirecting.
  if (loading) {
    return (
      <div style={{ padding: 40 }}>
        <Loading label="Loading..." />
      </div>
    );
  }

  // Not logged in → bounce to /login
  if (!user) {
    return <Navigate to="/login" replace state={{ from: loc }} />;
  }

  return (
    <Layout>
      <Routes>
        <Route path="/" element={<Dashboard />} />
        <Route
          path="/admin/organizations"
          element={
            <Protected requireRole="platform_admin">
              <AdminOrganizations />
            </Protected>
          }
        />
        <Route
          path="/users"
          element={
            <Protected>
              <RequireOrg>
                <Users />
              </RequireOrg>
            </Protected>
          }
        />
        <Route
          path="/api-keys"
          element={
            <Protected>
              <RequireOrg>
                <ApiKeys />
              </RequireOrg>
            </Protected>
          }
        />

        {/* ─── Library routes (single-version, no draft/live) ─────── */}
        <Route path="/library" element={<Navigate to="/library/global-rules" replace />} />
        {/* Legacy redirects for old /library/draft/* and /library/live/* URLs */}
        <Route path="/library/draft/*" element={<Navigate to={"/library/" + "*"} replace />} />
        <Route path="/library/live/*" element={<Navigate to={"/library/" + "*"} replace />} />

        <Route path="/library/global-rules" element={<LibraryRoute><LibraryGlobalRules /></LibraryRoute>} />
        <Route path="/library/specialties" element={<LibraryRoute><LibrarySpecialties /></LibraryRoute>} />
        <Route path="/library/specialties/new" element={<LibraryRoute><LibrarySpecialtyNew /></LibraryRoute>} />
        <Route path="/library/specialties/:specialty" element={<LibraryRoute><LibrarySpecialtyDetail /></LibraryRoute>} />
        <Route path="/library/specialties/:specialty/agents/:agent" element={<LibraryRoute><LibrarySpecialtyAgent /></LibraryRoute>} />
        <Route path="/library/agents" element={<LibraryRoute><LibraryAgents /></LibraryRoute>} />
        <Route path="/library/agents/new" element={<LibraryRoute><LibraryAgentNew /></LibraryRoute>} />
        <Route path="/library/agents/:id" element={<LibraryRoute><LibraryAgentRouter /></LibraryRoute>} />
        <Route path="/library/tools" element={<LibraryRoute><LibraryTools /></LibraryRoute>} />
        <Route path="/library/tools/new" element={<LibraryRoute><LibraryToolEdit /></LibraryRoute>} />
        <Route path="/library/tools/:id" element={<LibraryRoute><LibraryToolEdit /></LibraryRoute>} />
        <Route path="/library/protocols" element={<LibraryRoute><LibraryProtocols /></LibraryRoute>} />
        <Route path="/library/protocols/new" element={<LibraryRoute><LibraryProtocolNew /></LibraryRoute>} />
        <Route path="/library/protocols/:category/:id" element={<LibraryRoute><LibraryProtocolDetail /></LibraryRoute>} />
        <Route path="/library/post-call-actions" element={<LibraryRoute><LibraryPostCallActions /></LibraryRoute>} />
        <Route path="/library/built-in-agents" element={<LibraryRoute><LibraryChainFramework /></LibraryRoute>} />
        {/* Structured agent definition editor — library scope */}
        <Route path="/library/built-in-agents/:agentId" element={<LibraryRoute><StructuredAgentEditor /></LibraryRoute>} />
        <Route path="/library/agent-defs/:agentId" element={<LibraryRoute><StructuredAgentEditor /></LibraryRoute>} />

        {/* ─── Testing Setup ─────────────────────────────────────── */}
        <Route
          path="/testing-setup"
          element={
            <RequireOrg>
              <TestingSetup />
            </RequireOrg>
          }
        />

        {/* ─── Workspace (voice-driven HTML canvas demo) ─────────── */}
        {/* Standalone page — independent of /tenants/. Connects to
            the workspace runtime at ws://localhost:3009/workspace-ws. */}
        <Route
          path="/workspace"
          element={
            <RequireOrg>
              <WorkspacePage />
            </RequireOrg>
          }
        />

        {/* ─── Settings ─────────────────────────────────────────── */}
        <Route
          path="/settings/github"
          element={
            <RequireOrg>
              <GitHubSettings />
            </RequireOrg>
          }
        />

        {/* ─── Tenants ──────────────────────────────────────────── */}
        <Route
          path="/tenants"
          element={
            <RequireOrg>
              <Tenants />
            </RequireOrg>
          }
        />
        {/* /tenants/new and /tenants/updates must come BEFORE
            /tenants/:tid, otherwise the param route swallows them. */}
        <Route
          path="/tenants/new"
          element={
            <RequireOrg>
              <TenantNew />
            </RequireOrg>
          }
        />
        <Route
          path="/tenants/updates"
          element={
            <RequireOrg>
              <TenantUpdates />
            </RequireOrg>
          }
        />
        <Route
          path="/tenants/:tid"
          element={
            <RequireOrg>
              <TenantDetail />
            </RequireOrg>
          }
        />
        {/* Sub-tenant dual-environment routes: draft (editable) and
            live (read-only), mirroring the Library pattern. The bare
            path redirects to /draft so bookmarks and sidebar links
            always land in the editable environment. */}
        <Route
          path="/tenants/:tid/sub-tenants/:sid"
          element={<Navigate to="draft" replace />}
        />
        <Route
          path="/tenants/:tid/sub-tenants/:sid/draft"
          element={
            <RequireOrg>
              <SubTenantDetail />
            </RequireOrg>
          }
        />
        <Route
          path="/tenants/:tid/sub-tenants/:sid/live"
          element={
            <RequireOrg>
              <SubTenantDetail />
            </RequireOrg>
          }
        />
        <Route
          path="/tenants/:tid/sub-tenants/:sid/draft/prompts/:agent"
          element={<SubTenantPromptEdit />}
        />
        <Route
          path="/tenants/:tid/sub-tenants/:sid/live/prompts/:agent"
          element={<SubTenantPromptEdit />}
        />
        {/* Structured agent definition editor — sub-tenant scope */}
        <Route
          path="/tenants/:tid/sub-tenants/:sid/draft/agent-defs/:agentId"
          element={<StructuredAgentEditor />}
        />
        <Route
          path="/tenants/:tid/sub-tenants/:sid/live/agent-defs/:agentId"
          element={<StructuredAgentEditor />}
        />

        <Route path="*" element={<NotFound />} />
      </Routes>
    </Layout>
  );
}

// Helpers for legacy URL redirects that need path params

import { useParams } from "react-router-dom";
function RedirectToDraft({ segment }) {
  const params = useParams();
  // For /library/specialties/:specialty → /library/draft/specialties/:specialty
  const tail = params.specialty;
  return <Navigate to={`/library/draft/${segment}/${tail}`} replace />;
}
function RedirectToDraftAgent() {
  const { specialty, agent } = useParams();
  return (
    <Navigate
      to={`/library/draft/specialties/${specialty}/agents/${agent}`}
      replace
    />
  );
}
function RedirectToDraftProtocol() {
  const { category, id } = useParams();
  return (
    <Navigate
      to={`/library/draft/protocols/${category}/${id}`}
      replace
    />
  );
}
